import {FC, ReactNode, useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FEATURES, hasFeature, hasFeatureGrant, useMyActions} from "../../../../entities/my";
import {useGlobalMessageActions} from "../../../../entities/globalMessage";
import {IStudentData, StudentCard, studentService} from "../../../../entities/student";
import {MyError} from "../../../../shared/lib/error/MyError";
import {AuthError} from "../../../../shared/lib/error/AuthError";
import classes from './studentWidget.module.scss'
import {StudentCalendar} from "../studentCalendar/StudentCalendar";
import {useAppSelector} from "../../../../app/store/store";
import {Header} from "../header/Header";
import {DataList} from "../../../../shared/ui/dataList";
import {StudentsMove} from "../../../../features/studentsMove";
import {ITutor} from "../../../../entities/tutor";
import {ChangeStudentBalance} from "../../../../features/changeStudentBalance";
import {LoaderSpinner} from "../../../../shared/ui/spinner";
import {ChangeStudentPayment} from "../../../../features/changeStudentPayment";
import {IPayment} from "../../../../entities/admin";
import {StudentTags} from "../tags/StudentTags";
import {OpenContainer} from "../../../../features/openContainer";
import idmClasses from "../../../idm/ui/idm/idmWidget.module.scss";
import {STUDENTS_ROUTE} from "../../../../app/router/routes";

interface IProps {
    id: number;
    content?: (student: IStudentData) => ReactNode;
    afterContent?: (student: IStudentData) => ReactNode;
    afterLessons?: (student: IStudentData) => ReactNode;
}

export const StudentWidget: FC<IProps> = ({id, content, afterContent, afterLessons}) => {

    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [student, setStudent] = useState<IStudentData>()
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const setBalance = (balance: string) => {
        const copy: IStudentData = JSON.parse(JSON.stringify(student))
        copy.balance = balance;
        setStudent(copy)
    }

    const {my} = useAppSelector(s => s.myReducer)
    const getData = useCallback(async () => {
        try {
            if (id) {
                setIsLoading(true)
                setStudent(undefined)
                const studentRes = await studentService.get(+id)
                setStudent(studentRes)
            }
        } catch (e) {
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                if (e.status === 403) {
                    setGlobalMessage({message: e.message || 'Нет доступа к карточке студента', type: 'error'})
                    navigate(STUDENTS_ROUTE.path, {replace: true})
                    return
                }
                setGlobalMessage({message: e.message, type: 'error'})
            } else {
                setGlobalMessage({message: 'Ошибка при получении данных о студенте', type: 'error'})
            }
        } finally {
            setIsLoading(false)
        }
    }, [id, navigate, setGlobalMessage, setIsAuth])

    useEffect(() => {
        getData()
    }, [getData])

    const onNewTutor = (tutor: ITutor) => {
        if (student)
            setStudent({
                ...student,
                user_id: tutor.user_id ?? tutor.id,
                tutor_name: tutor.full_name
            })
    }

    const setStudentPayment = (payment: IPayment) => {
        if (student)
            setStudent({...student, payment_id: payment.payment_id, payment_name: payment.payment_name})
    }

    const targetUserId = Number(student?.user_id ?? 0)
    const canReadStudent = Boolean(student)
        && hasFeature(my, FEATURES.STUDENTS.CARD_READ, targetUserId)
    const canViewFullStudent = canReadStudent
        && hasFeature(my, FEATURES.STUDENTS.VIEW_FULL, targetUserId)
    const canViewTutorStudent = canReadStudent
        && hasFeature(my, FEATURES.STUDENTS.VIEW_TUTOR, targetUserId)
    const canChangeTutor = canViewFullStudent
        && hasFeature(my, FEATURES.STUDENTS.CARD_TUTOR_CHANGE, targetUserId)
        && hasFeatureGrant(my, FEATURES.IDM.STAFF_LIST)
    const canChangePayment = canViewFullStudent
        && hasFeature(my, FEATURES.STUDENTS.CARD_PAYMENT_UPDATE, targetUserId)
    const canChangeBalance = canViewFullStudent
        && hasFeature(my, FEATURES.STUDENTS.CARD_WALLET_UPDATE, targetUserId)
    const canViewStudentTags = canViewFullStudent
        && hasFeature(my, FEATURES.STUDENTS.TAGS_STUDENT_LIST, targetUserId)
    const canViewStudentFinance = canViewFullStudent
        && hasFeature(my, FEATURES.STUDENTS.CARD_FINANCE_READ, targetUserId)
    const studentContent = student ? content?.(student) : null
    const studentAfterLessons = student ? afterLessons?.(student) : null
    const studentAfterContent = student ? afterContent?.(student) : null

    return (
        <section className={classes.container}>
            {
                isLoading
                    ?
                <section className={classes.loader}><LoaderSpinner/></section>
                    :
                student 
                    && 
                (canViewFullStudent
                    ?
                <section className={classes.detailPage}>
                    <section className={`${idmClasses.panel} ${classes.heroPanel}`}>
                        <Header
                            student={student}
                            setStudent={setStudent}
                            extraActions={canChangeTutor ? (
                                <StudentsMove
                                    title="Смена исполнителя у студента"
                                    onSelected={onNewTutor}
                                    tutorId={Number(student.user_id)}
                                    studentId={student.id}
                                />
                            ) : undefined}
                        />
                        <section className={classes.dataOpenBlock}>
                            <OpenContainer
                                title="Данные"
                                icon={<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="3" /><path d="M5 20C5 16.7 8.1 14 12 14C15.9 14 19 16.7 19 20" /></svg>}
                            >
                                <StudentCard
                                    student={student}
                                    hideTitles
                                    paymentChange={canChangePayment ?
                                        <ChangeStudentPayment
                                            payment_id={student.payment_id}
                                            payment_name={student.payment_name}
                                            studentId={student.id}
                                            setStudentPayment={setStudentPayment}
                                        /> : undefined
                                    }
                                    balanceChange={canChangeBalance ?
                                        <ChangeStudentBalance
                                            studentId={student.id}
                                            setStudentBalance={setBalance}
                                        /> : undefined
                                    }
                                />
                            </OpenContainer>
                        </section>
                    </section>
                    {
                        canViewStudentTags 
                            &&
                        <section className={classes.sectionBlock}>
                            <StudentTags
                                studentId={student.id}
                                targetUserId={targetUserId}
                                tags={student.tags}
                                setTags={tags => setStudent(current => current ? {
                                    ...current,
                                    tags
                                } : current)}
                            />
                        </section>
                    }
                    {
                        canViewStudentFinance
                            &&
                        <section className={classes.sectionBlock}>
                            <section className={idmClasses.panel}>
                                <StudentCalendar id={student.id}/>
                            </section>
                        </section>
                    }
                    {
                        studentContent 
                            &&
                        <section className={classes.sectionBlock}>
                            <section className={idmClasses.panel}>
                                <section className={classes.lessons}>
                                    {studentContent}
                                </section>
                            </section>
                        </section>
                    }
                    {studentAfterLessons}
                    {studentAfterContent}
                </section>
                    :
                canViewTutorStudent 
                    ? 
                <section className={classes.tutor}>
                    <DataList
                        title="Данные студента"
                        list={[
                            `ID: ${student.id}`,
                            `Имя: ${student.last_name} ${student.first_name} ${student.middle_name}`,
                        ]}
                    />
                    {studentContent && <section className={classes.lessons}>{studentContent}</section>}
                    {
                        studentAfterLessons
                            &&
                        <section className={classes.tutorCalendar}>
                            {studentAfterLessons}
                        </section>
                    }
                </section> 
                    : 
                null)
            }
        </section>
    )
}
